import { ITEM_NOTE_MAX_LENGTH } from '@/components/page-specific/catering/common/helpers/useItemNotesRemovedByFlag'
import CommentIcon from '@mui/icons-material/Comment'
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined'
import { Button, IconButton, Popover, Stack, TextField, Typography } from '@mui/material'

import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ItemNotePopoverProps {
  note: string
  onSave: (note: string) => void
  onRemove: () => void
}

export function ItemNotePopover(props: ItemNotePopoverProps) {
  const { note, onSave, onRemove } = props
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [draftNote, setDraftNote] = useState(note)
  const hasNote = note.length > 0

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDraftNote(note)
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => setAnchorEl(null)

  const handleSave = () => {
    const trimmedNote = draftNote.trim()
    if (trimmedNote !== note) onSave(trimmedNote)
    handleClose()
  }

  const handleRemove = () => {
    onRemove()
    handleClose()
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        aria-label={hasNote ? t('marketplace:editNote') : t('marketplace:addNote')}
        data-cy="basket-item-note-button">
        {hasNote ? (
          <CommentIcon sx={{ transform: 'scale(0.75)' }} />
        ) : (
          <CommentOutlinedIcon sx={{ transform: 'scale(0.75)' }} />
        )}
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Stack spacing={1.5} sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2">
            {hasNote ? t('marketplace:editNote') : t('marketplace:addNote')}
          </Typography>
          <TextField
            multiline
            minRows={3}
            fullWidth
            size="small"
            label={t('marketplace:notes')}
            value={draftNote}
            onChange={(e) => setDraftNote(e.target.value.slice(0, ITEM_NOTE_MAX_LENGTH))}
            slotProps={{ htmlInput: { maxLength: ITEM_NOTE_MAX_LENGTH } }}
            helperText={`${draftNote.length}/${ITEM_NOTE_MAX_LENGTH}`}
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            {hasNote && (
              <Button
                size="small"
                color="error"
                onClick={handleRemove}
                data-cy="basket-item-note-remove">
                {t('marketplace:remove')}
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              data-cy="basket-item-note-save">
              {t('marketplace:save')}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  )
}
